/**
 * QuoteVersionTimeline — Vertical timeline of quotation version history.
 * SALES-P2-3: QuoteVersion Diff UI
 *
 * Shows each version as a timeline node with version number, date, status.
 * Click to select a version for comparison. Current version is highlighted.
 */

import type { QuotationVersionEntry } from '@/api/quotations';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui';
import { useTranslation } from '@kgc/i18n';
import { QUOTATION_STATUSES } from '../mock-data';

// ============================================
// TYPES
// ============================================

interface QuoteVersionTimelineProps {
  versions: QuotationVersionEntry[];
  currentVersionId: string;
  selectedIds: [string | null, string | null];
  onSelectVersion: (id: string) => void;
}

// ============================================
// HELPERS
// ============================================

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatPrice(price: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(price);
}

// ============================================
// COMPONENT
// ============================================

export function QuoteVersionTimeline({
  versions,
  currentVersionId,
  selectedIds,
  onSelectVersion,
}: QuoteVersionTimelineProps) {
  const { t } = useTranslation();

  if (versions.length === 0) {
    return null;
  }

  // Sort by version descending (newest first)
  const sorted = [...versions].sort((a, b) => b.version - a.version);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('quotation.diff.versionHistory')}</CardTitle>
        <p className="text-sm text-muted-foreground">{t('quotation.diff.selectVersions')}</p>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-slate-600" />

          <div className="space-y-0">
            {sorted.map((version, idx) => {
              const isCurrent = version.id === currentVersionId;
              const isSelectedA = selectedIds[0] === version.id;
              const isSelectedB = selectedIds[1] === version.id;
              const isSelected = isSelectedA || isSelectedB;
              const statusConfig = QUOTATION_STATUSES.find(s => s.value === version.status);
              const isLast = idx === sorted.length - 1;

              return (
                <div
                  key={version.id}
                  className={`relative flex items-start gap-4 cursor-pointer rounded-lg p-3 transition-colors
                    ${
                      isSelected
                        ? 'bg-indigo-50 dark:bg-indigo-900/20 ring-1 ring-indigo-300 dark:ring-indigo-700'
                        : isCurrent
                          ? 'bg-gray-50 dark:bg-slate-700/30'
                          : 'hover:bg-gray-50 dark:hover:bg-slate-700/20'
                    }`}
                  onClick={() => onSelectVersion(version.id)}
                >
                  {/* Timeline dot */}
                  <div className="relative z-10 flex-shrink-0">
                    <div
                      className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold
                        ${
                          isSelected
                            ? 'bg-indigo-600 text-white'
                            : isCurrent
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-gray-200 text-gray-600 dark:bg-slate-600 dark:text-gray-300'
                        }`}
                    >
                      v{version.version}
                    </div>
                    {/* Connector line to next */}
                    {!isLast && (
                      <div className="absolute left-1/2 top-8 h-3 w-0.5 -translate-x-1/2 bg-gray-200 dark:bg-slate-600" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-foreground">
                        {version.quotationNumber}
                      </span>
                      {statusConfig && (
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusConfig.color}`}
                        >
                          {t(statusConfig.labelKey)}
                        </span>
                      )}
                      {isCurrent && (
                        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300">
                          {t('quotation.currentVersion')}
                        </span>
                      )}
                      {isSelectedA && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                          A
                        </span>
                      )}
                      {isSelectedB && (
                        <span className="rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/30 dark:text-orange-300">
                          B
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{formatDate(version.createdAt)}</span>
                      <span className="font-medium">{formatPrice(version.totalGross)}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
